import { Injectable } from '@angular/core';
import { BasespaceService, V2AppSessionCompactList, V2AppSession, V2Dataset, V1pre3ProjectCompact } from '@bssh/ng-sdk';
import { BehaviorSubject, EMPTY, forkJoin, interval, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, delay, finalize, map, retryWhen, shareReplay, startWith, switchMap, takeWhile } from 'rxjs/operators';
import { isNullOrUndefined } from 'util';
import { AppSessionExecutionStatus, IAppSession, SortBy, SortDir } from '../../model/appSessions/appSession';
import { IApiResponseWrapper, IBulkUpdateResponse, IErrorState } from '../../model/v2-api/v2-api-wrappers';
import { genericRetryWhen, observableEmitDelay } from '../../rxjsutils/rxjs-utilities';
import { PageContextService } from '../../services/page-context.service';
import { AbstractResourceStore } from '../resource/abstract.resource.store';
import { IAnalysesState } from './analyses.state';
import { ChangeDeliveryStatusService } from '../../../analyses/services/change-delivery-status.service';
import { AnalysisDeliveryStatus, AnalysisStatus } from '../../model/appSessions/analysis-status';
import { AnalysisQCStatus } from '@app/core/model/appSessions/analysis-status';
import { ChangesAnalysisQcStatusService } from '@app/analyses/services/change-analysis-qc-status.service';
import { TrashService } from '@app/core/services/toolbar/trash.service';
import { ErrorMessages } from '@app/core/utilities/error-messages';
import { ToastrMessages } from '@app/core/utilities/toastr-messages';
import { ToastrService } from '@bssh/comp-lib';
import { ArrayUtilities } from '@app/core/utilities/array-utilities';
import { DownloadUrl } from '../../model/download-url';
import { DownloadService } from '../../services/toolbar/download.service';
import { ResourceType } from '@app/core/model/resource-type';
import { CancelAnalysisService } from '@app/analyses/services/cancel-analysis.service';
import { PropertyName } from '../../model/properties/property-name';
import { PropertyService } from '@app/core/services/property/property.service';
import { BsApiService } from '@app/core/services/bs-api/bs-api-service';
import { ConsoleLogger } from '@app/core/utilities/consolelogger';
import _, { get } from 'lodash';
import { IDataset } from '@app/core/model/datasets/dataset';
import { IV2AppSessionReport } from '@app/core/model/appSessions/appsession-report';
import { UPLOADER_APPLICATION_ID } from '@app/core/model/applications/applications';
import { StringUtilities } from '@app/core/utilities/string-utilities';
import environment from '@environments/environment';
import { HttpUtilityService } from '@app/core/services/http-utility/http-utility.service';
import { AnalysisUtilityService } from '@app/analyses/services/analysis-utility.service';

export interface IAnalysesStore {
  canChangeDeliveryStatus(): boolean;
  changeDeliveryStatus(newStatus: AnalysisDeliveryStatus, comments: string): void;
  loadCurrentAnalysis(id: string, forceLoad: boolean): void;
  canChangeQCStatus(): boolean;
  changeQCStatus(newStatus: AnalysisQCStatus, comments?: string): void;
  loadAnalysesList(offset: number, limit: number, sortby: string, sortdir: string,
    propertyfilters: string[], forceLoad: boolean): void;
  unloadCurrentAnalysis(): void;
  getActiveList(): IAppSession[];
  canCancelAnalysis(): boolean;
  cancelAnalysis(): void;
  getAnalysisDefaultProjectId(): string;
  getAnalysisOutputDatasets(): Observable<IDataset[]>
}

@Injectable({
  providedIn: 'root'
})
export class AnalysesStore extends AbstractResourceStore<IAnalysesState> implements IAnalysesStore {
  // pass in the state slices we are interested in...

  private refreshSubject = new BehaviorSubject<boolean>(false);
  analysesListRefreshed$ = this.refreshSubject.asObservable();

  constructor(private pageContextService: PageContextService, private basespaceApi: BasespaceService,
    private toastrService: ToastrService, private changDeliveryStatusService: ChangeDeliveryStatusService,
    private changesAnalysisQcStatusService: ChangesAnalysisQcStatusService, private downloadService: DownloadService,
    private trashService: TrashService, private cancelAnalysisService: CancelAnalysisService,
    private propertyService: PropertyService, private bsApi: BsApiService,
    private httpUtilityService: HttpUtilityService,
    private analysisUtilityService: AnalysisUtilityService
  ) {
    super(['analysesList', 'analysesOffset', 'analysesLimit', 'analysesSortBy', 'analysesSortDir', 'selectedAnalyses',
      'currentAnalysis', 'currentAnalysisReport', 'currentAnalysisOutputProjects', 'currentUser', 'currentUserId',
      'currentUserCodeFeatures', 'analysisStateError']);
  }

  /**
   * Calls BSSH API to load analysis details and cache in the  store
   * @param id Id of the analysis to fetch. Will not fetch if the state's currentAnalysis already matches this id.
   */
  public loadCurrentAnalysis(id: string, forceLoad: boolean = false): void {

    const currentState = this.getState();
    if (currentState && currentState.currentAnalysis && currentState.currentAnalysis.Id === id && !forceLoad) {
      // if analysis already set don't fetch, but dispatch current state if another call is made later
      this.dispatchCurrentState(currentState);
    } else {
      this.loadingSubject.next(true);

      // Subscribe to retrieve
      this.subs.sink = this.getAppSessionAndReportsObservable(id)
        .pipe(finalize(() => this.loadingSubject.next(false)))
        .subscribe({
          next: ([v2AppSession, v2AppSessionReport, outputProjects]) => {
            this.setState({
              currentAnalysis: v2AppSession,
              currentAnalysisReport: v2AppSessionReport,
              currentAnalysisOutputProjects: outputProjects
            }, AnalysisStoreActions.LoadAnalysisDetails);
          },
          error: error => this.handleError(error, () => ({ ...currentState, analysisStateError: error }))
        });
    }
  }

  public pollCurrentAnalysis(): Observable<IAppSession> {
    const currentState = this.getState();
    const currentAnalysis = currentState.currentAnalysis;

    return interval(30000)
      .pipe(
        takeWhile(() => !this.isAppSessionTerminal(currentAnalysis)),
        switchMap(() => this.getAppSessionAndReportsObservable(currentAnalysis.Id)),
        map(([v2AppSession, v2AppSessionReport, outputProjects]) => {
          this.setState({
            currentAnalysis: v2AppSession,
            currentAnalysisReport: v2AppSessionReport,
            currentAnalysisOutputProjects: outputProjects
          }, AnalysisStoreActions.LoadAnalysisDetails);
          return v2AppSession;
        })
      );
  }

  public getAppSessionObservableWithInclude(appSessionId: string,
    includeParam: Array<'Children' | 'Logs' | 'RelatedUserWithAccess'>): Observable<IAppSession> {

    let params: BasespaceService.GetV2AppsessionsIdParams;

    if (includeParam) {
      params = { id: appSessionId, include: includeParam }
    } else {
      params = { id: appSessionId }
    }

    return this.basespaceApi.GetV2AppsessionsId(
      params).pipe(
        // Do not make an additional API call if any additional subscribers have subscribed later on
        shareReplay(1),
        // Retry in case of HTTP errors
        retryWhen(genericRetryWhen()),
        // To avoid a Flash of content, maintain a delay
        delay(observableEmitDelay)
      );
  }

  /**
   * Unload current analysis data to avoid stale data displayed when enter another analysis details page
   */
  public unloadCurrentAnalysis() {
    this.setState({ currentAnalysis: undefined });
  }

  /**
   * Calls BSSH API to load a list of anaylses and cache in the analyses store as 'analysesList'
   * @param id Id of the analysis to fetch. Will not fetch if the state's currentAnalysis already matches this id.
   */
  public loadAnalysesList(offset: number = 0, limit: number = 10, sortby: SortBy = 'ModifiedOn',
    sortdir: SortDir = 'Desc', propertyfilters: string[] = ['Output.Projects'], forceLoad: boolean = false) {

    const currentState = this.getState();
    if (currentState && currentState.analysesOffset === offset && currentState.analysesLimit === limit
      && currentState.analysesSortBy === sortby && currentState.analysesSortDir === sortdir && !forceLoad) {

      // if same settings, use the list we already have cached
      this.dispatchCurrentState(currentState);
    } else {
      this.loadingSubject.next(true);

      this.subs.sink = this.basespaceApi.GetV2Appsessions({ sortdir, offset, limit, sortby, propertyfilters })
        .pipe(
          // Retry in case of HTTP errors
          retryWhen(genericRetryWhen()),
          // To avoid a Flash of content, maintain a delay
          delay(observableEmitDelay),
          // Stop loading in finalize.
          finalize(() => this.loadingSubject.next(false))
        ).subscribe({
          next: (apiResponse: V2AppSessionCompactList) => {
            const analysesList = apiResponse.Items;

            // save the list and the paging params
            // TODO also filters, when that feature is ready
            this.setState({
              analysesList,
              analysesOffset: apiResponse.Paging.Offset,
              analysesLimit: apiResponse.Paging.Limit,
              analysesSortBy: apiResponse.Paging.SortBy,
              analysesSortDir: apiResponse.Paging.SortDir,
              analysisStateError: null,
              selectedAnalyses: null
            }, AnalysisStoreActions.LoadAnalysesList);
            this.loadingSubject.next(false);
          },
          error: error => {
            this.handleError(error, () => ({ ...this.getState(), analysisStateError: error }));
            this.loadingSubject.next(false);
          }
        });
    }
  }

  public updateSelectedAnalyses(updatedSelections: IAppSession[]) {
    const analyses = updatedSelections;
    this.setState({ selectedAnalyses: analyses }, AnalysisStoreActions.UpdateSelectedAnalyses);
  }

  public canChangeDeliveryStatus(): boolean {
    const { selectedAnalyses, currentAnalysis } = { ...this.getState() };
    const analyses = selectedAnalyses ? selectedAnalyses : [currentAnalysis];
    return this.changDeliveryStatusService.canChangeAnalysisDeliveryStatus(analyses);
  }

  public changeDeliveryStatus(newStatus: AnalysisDeliveryStatus, comments: string): void {
    const { selectedAnalyses, currentAnalysis } = { ...this.getState() };
    const analyses = selectedAnalyses ? selectedAnalyses : [currentAnalysis];

    this.loadingSubject.next(true);

    this.changDeliveryStatusService.changeAnalysisDeliveryStatus(analyses, newStatus, comments).pipe(
      finalize(() => this.loadingSubject.next(false))
    ).subscribe({
      next: (apiResponses: IBulkUpdateResponse<IAppSession>) => {
        const totalSuccessCount = apiResponses.TotalSuccessCount;
        const totalFailureCount = apiResponses.TotalFailureCount;

        const updatedAnalyses = [];
        if (totalSuccessCount > 0) {
          apiResponses.SuccessItems.forEach(item => {
            const analysis = analyses.find(a => a.Id === item.Id);
            updatedAnalyses.push(analysis);
          });
        }

        let deliveryStatusUpdateError: IErrorState = null;
        if (totalFailureCount > 0) {
          deliveryStatusUpdateError = {
            errorMessage: ErrorMessages.FAILED_TO_UPDATE_ANALYSIS_DELIVERY_STATUS,
            data: apiResponses.FailureItems
          };
        }

        this.setStateOnSuccess({ DeliveryStatus: newStatus },
          updatedAnalyses,
          AnalysisStoreActions.ChangeDeliveryStatus,
          deliveryStatusUpdateError,
          this.pageContextService.isAnalysisMasterList() || this.pageContextService.isSubResourceAnalysisList());

        if (totalFailureCount === 0) {
          this.toastrService.success(ToastrMessages.ANALYSES_DELIVERY_STATUS_CHANGED);
        }
      },
      error: error => {
        this.handleError(error, () => ({ ...this.getState(), laneStateError: error }));
      }
    });
  }

  public getActiveList(): IAppSession[] {
    const { currentAnalysis, selectedAnalyses } = this.getState();
    if (this.pageContextService.isAnalysisMasterList() || this.pageContextService.isSubResourceAnalysisList()) {
      return selectedAnalyses;
    }

    if (isNullOrUndefined(currentAnalysis)) {
      return null;
    }

    return [currentAnalysis];
  }

  public canChangeQCStatus(): boolean {
    const activeAnalyses = this.getActiveList();

    if (ArrayUtilities.isArrayOfSize(activeAnalyses, 0)) {
      return false;
    }

    return this.changesAnalysisQcStatusService.canChangeAnalysisQCStatus(activeAnalyses);
  }

  public changeQCStatus(newStatus: AnalysisQCStatus, comments?: string): void {
    const activeAnalyses = this.getActiveList();

    if (ArrayUtilities.isArrayOfSize(activeAnalyses, 0)) {
      return;
    }

    this.loadingSubject.next(true);
    this.changesAnalysisQcStatusService.changeAnalysisQCStatus(activeAnalyses, newStatus, comments).pipe(
      finalize(() => this.loadingSubject.next(false))
    ).subscribe({
      next: (response: IBulkUpdateResponse<IAppSession>) => {
        let qcStatusUpdateError: IErrorState = null;
        const updatedAnalyses = activeAnalyses;
        if (response.TotalFailureCount > 0) {
          const error = response.FailureItems[0].ErrorMessage;
          qcStatusUpdateError = {
            errorMessage: isNullOrUndefined(error) ? ErrorMessages.FAILED_TO_UPDATE_ANALYSIS_QC_STATUS : error,
            data: response.FailureItems
          };
        }

        response.SuccessItems.forEach(updatedAnalysis => {
          const index = activeAnalyses.findIndex(analysis => analysis.Id === updatedAnalysis.Id);
          Object.assign(updatedAnalyses[index], updatedAnalysis);
        });

        this.setStateOnSuccess({ QcStatus: newStatus }, updatedAnalyses,
          AnalysisStoreActions.UpdateSelectedAnalyses, qcStatusUpdateError, this.pageContextService.isAnalysisMasterList() || this.pageContextService.isSubResourceAnalysisList());

        if (response.TotalFailureCount === 0) {
          this.toastrService.success(ToastrMessages.ANALYSES_QC_STATUS_CHANGED);
        }
      },
      error: error => {
        this.handleError(error, () => ({ ...this.getState(), analysisStateError: error }));
      }
    });
  }

  public canRequeue(): boolean {
    const activeAnalyses = this.getActiveList();
    return ArrayUtilities.isArrayOfSize(activeAnalyses, 1) && 
      this.analysisUtilityService.isAnalysisInputFormDataAvailable(activeAnalyses[0]) &&
      !!activeAnalyses[0].Application && 
      // '10' is always id for import application. No reason to requeue an import app
      activeAnalyses[0].Application.Id !== '10';
  }

  public canTrash(): boolean {
    const activeAnalyses = this.getActiveList();
    const { currentUserId } = this.getState();
    if (this.isTrashRestoreInProgress(activeAnalyses)) {
      return false;
    }

    return this.trashService.canTrash(activeAnalyses, currentUserId);
  }

  /**
   * Calls the TrashService to Trash the Runs after checking the status
   * @param deleteOption Option to trash the Data Files only
   */
  public trash(option?: any): void {

    const activeAnalyses = this.getActiveList();

    if (!activeAnalyses) {
      throw new Error(ErrorMessages.MISSING_RESOURCE_ERROR);
    }

    this.loadingSubject.next(true);

    const analysesIds = activeAnalyses.map(a => a.Id);

    this.trashAppSessions(analysesIds, activeAnalyses);
  }

  /**
   * delete those app sessions
   */
  public trashAppSessions(analysesIds: string[], analysesToBeTrashed: IAppSession[]) {


    // trash app sessions of recieved ids
    this.trashService.trashAppSessions(analysesIds).pipe(
      finalize(() => this.loadingSubject.next(false))
    ).subscribe({
      next: (appSessionTrashResponse) => {
        const totalSuccessCount = appSessionTrashResponse.TotalSuccessCount;
        const totalFailureCount = appSessionTrashResponse.TotalFailureCount;

        // getting analyses to update that were successfully trashed
        const deletedAnalyses: IAppSession[] = [];
        if (totalSuccessCount > 0) {
          appSessionTrashResponse.SuccessItems.forEach(item => {
            const analyses = analysesToBeTrashed.find(a => a.Id === item.Response.DeletedAppSession.Id);
            deletedAnalyses.push(analyses);
          });
        }

        let trashError = null;
        if (totalFailureCount > 0) {
          trashError = [];
          // load trash error with app sessions error,
          // only app session errors will be there
          appSessionTrashResponse.FailureItems.forEach(item => {
            trashError.push({
              errorMessage: item.ErrorMessage,
              data: item,
            });
          });
        }

        this.setStateOnSuccess(
          {
            IsDeleted: true,
            IsFileDataDeleted: true
          }
          , deletedAnalyses,
          AnalysisStoreActions.DeleteAnalyses,
          trashError,
          totalSuccessCount > 0
        );

        // showing toaster message on success only. Errors are shown in modal itself
        if ((!trashError || trashError.length === 0)) {
          this.toastrService.success(ToastrMessages.TRASH_ANALYSIS_MESSAGE(totalSuccessCount));
        }

      },
      error: error => {
        this.handleError(error, () => ({ ...this.getState(), analysisStateError: error }));
        this.loadingSubject.next(false);
      }
    });
  }

  public canDownload(): boolean {
    const selectedAnalyses = this.getActiveList();

    return !isNullOrUndefined(selectedAnalyses) && selectedAnalyses.length > 0 && !this.isTrashRestoreInProgress(selectedAnalyses);
  }

  public isTrashRestoreInProgress(selectedAnalyses: IAppSession[]): boolean {
    if (isNullOrUndefined(selectedAnalyses)) {
      return false;
    }

    selectedAnalyses.every(selectedAnalysis => {
      return (selectedAnalysis.IsTrashRestoreInProgress) ? true : false;
    });
    return false;
  }

  /**
   * Calls BSSH API to get Run Download Uri.
   */
  public getDownloadUri(): Observable<DownloadUrl> {
    this.loadingSubject.next(true);
    const activeAnalyses = this.getActiveList();
    const analysisIds = activeAnalyses.map((analysis) => analysis.Id);

    return this.downloadService.getDownloadUri(analysisIds, ResourceType.APP_SESSION).pipe(
      finalize(() => {
        this.loadingSubject.next(false);
      })
    );
  }

  public edit(newAnalysesName: string): void {
    // note: using Id throughout to call v1 APIs
    if (!this.canEdit()) {
      // re-check, make no state change if edit analyses name not allowed.
      return;
    }

    this.loadingSubject.next(true);
    const activeAnalyses = this.getActiveList();

    this.subs.sink = this.editAnalysesName(newAnalysesName, activeAnalyses[0]).pipe(
      finalize(() => this.loadingSubject.next(false))
    ).subscribe({
      next: (apiResponse: V2AppSession) => {
        this.setStateOnSuccess({ Name: apiResponse.Name },
          activeAnalyses, AnalysisStoreActions.EditAnalysesName, null, this.pageContextService.isAnalysisMasterList() ||
        this.pageContextService.isSubResourceAnalysisList());
      },
      error: error => {
        this.handleError(error, () => ({ ...this.getState(), analysisStateError: error }));
      }
    });

  }

  public canEdit(): boolean {
    const activeAnalyses = this.getActiveList();
    return ArrayUtilities.isArrayOfSize(activeAnalyses, 1);
  }

  public clearSelectedAnalyses(): void {
    const currentState = this.getState();
    if (currentState != null && currentState.selectedAnalyses != null) {
      this.setState({ selectedAnalyses: null, analysisStateError: null }, AnalysisStoreActions.ClearSelectedAnalyses);
    }
  }

  public canCancelAnalysis(): boolean {
    const activeAnalyses = this.getActiveList();
    return this.cancelAnalysisService.canCancelAnalysis(activeAnalyses);
  }

  public cancelAnalysis() {
    if (!this.canCancelAnalysis()) {
      return;
    }
    const activeAnalyses = this.getActiveList();
    this.loadingSubject.next(true);

    this.cancelAnalysisService.cancelAnalysis(activeAnalyses[0]).pipe(
      finalize(() => this.loadingSubject.next(false))
    ).subscribe({
      next: (apiResponses: IAppSession) => {
        this.setStateOnSuccess({ Status: AnalysisStatus.Canceled }, [apiResponses], AnalysisStoreActions.CancelAnalyses, null,
          this.pageContextService.isAnalysisMasterList() || this.pageContextService.isSubResourceAnalysisList());
        this.toastrService.success(ToastrMessages.ANALYSES_CANCELED);
      },
      error: error => {
        this.handleError(error, () => ({ ...this.getState(), analysisStateError: error }));
      }
    });
  }

  public getAnalysisOutputProjects(): V1pre3ProjectCompact[] {

    const currentState = this.getState();
    const outputProjects = currentState.currentAnalysisOutputProjects;

    return outputProjects;
  }

  /**
   * currentAnalysis already have the properties list. We don't need to call api
   */
  public getAnalysisDefaultProjectId(): string {
    const outputProjects = this.getAnalysisOutputProjects();

    if (outputProjects.length > 0) {
      return outputProjects[0].Id;
    }

    return null;
  }

  public getProperty(appSessionId: string,
    propertyName: string,
    propertyItemLimit: number) {

    // propertyItemLimit must be between 0 and 2000 (upper limit on appsession datasets to fetch: BASE-70699)
    if (propertyItemLimit < 0 || propertyItemLimit > 2000) {
      ConsoleLogger.logWarning(`Modifying propertyItemLimit ${propertyItemLimit} to be in valid range (0 - 2000)`);
    }
    propertyItemLimit = Math.min(2000, Math.max(0, propertyItemLimit));

    return this.bsApi.getAppsessionPropertyById(appSessionId, propertyName, propertyItemLimit).pipe(
      // Do not make an additional API call if any additional subscribers have subscribed later on
      shareReplay(1),
      // Retry in case of HTTP errors
      retryWhen(genericRetryWhen()),
      // To avoid a Flash of content, maintain a delay
      delay(observableEmitDelay),
      // Stop loading in finalize.
      finalize(() => this.loadingSubject.next(false))
    );
  }

  public getAnalysisOutputDatasets(): Observable<IDataset[]> {
    this.loadingSubject.next(true);
    const activeAnalyses = this.getActiveList();

    if (isNullOrUndefined(activeAnalyses)) {
      return of(null);
    }

    return this.getProperty(activeAnalyses[0].Id, PropertyName.OUTPUT_DATASETS, 1000).pipe(
      finalize(() => this.loadingSubject.next(false)),
      map((propertyItems) => {
        if (propertyItems && propertyItems.DatasetItems) {
          return propertyItems.DatasetItems.filter(x => !_.isEmpty(x.Attributes) && !x.IsFileDataDeleted);
        }
        return of(null);
      }),
      catchError(error => {
        ConsoleLogger.logError(error);
        return of(null);
      })
    );
  }

  public getAppsessionById(): Observable<V2AppSession> {
    const activeAnalyses = this.getActiveList();

    if (isNullOrUndefined(activeAnalyses)) {
      return of(null);
    }

    const currentAnalysis = activeAnalyses[0];
    const appSession$ = this.basespaceApi.GetV2AppsessionsId({ id: currentAnalysis.Id });
    return appSession$.pipe(
      finalize(() => this.loadingSubject.next(false)),
      map((appSessionResponse: V2AppSession) => {
        if (appSessionResponse) {
          return appSessionResponse;
        }
      }),
      catchError((error) => {
        ConsoleLogger.logError(error);
        return of(null);
      })
    );
  }

  public getResourceType(): ResourceType {
    return ResourceType.ANALYSIS;
  }

  public isCreatedByCurrentUser(appSession: IAppSession): boolean {
    const { currentUserId } = this.getState();
    const userCreatedById = get(appSession, 'UserCreatedBy.Id');

    return userCreatedById === currentUserId;
  }

  public shouldRedirectToUploadPage(appSession: IAppSession): boolean {
    const applicationId = get(appSession, 'Application.Id');
    const appSessionStatus = get(appSession, 'ExecutionStatus');
    const isAppSessionRunning = appSessionStatus === AnalysisStatus.Running;
    const isCreatedByCurrentUser = this.isCreatedByCurrentUser(appSession);

    return applicationId === UPLOADER_APPLICATION_ID && isCreatedByCurrentUser && isAppSessionRunning;
  }

  public shouldRedirectToReportsTab(uiReportDefaultRelativePath: string): boolean {
    if (uiReportDefaultRelativePath) {

      const reportDefaultFullUrl = `${StringUtilities.trimTrailingSlash(environment.siteUrl)}/${uiReportDefaultRelativePath}`;
      const pathSegments = this.httpUtilityService.getPathSegments(reportDefaultFullUrl);
      const hasSummary = pathSegments.some(item => item.includes("summary"));

      if (hasSummary) {
        return true;
      }
    }

    return false;
  }

  public isAppSessionTerminal(v2AppSession: IAppSession): boolean {
    switch (v2AppSession.ExecutionStatus) {
      case AppSessionExecutionStatus[AppSessionExecutionStatus.Aborted]:
      case AppSessionExecutionStatus[AppSessionExecutionStatus.NeedsAttention]:
      case AppSessionExecutionStatus[AppSessionExecutionStatus.Complete]:
      case AppSessionExecutionStatus[AppSessionExecutionStatus.TimedOut]:
      case AppSessionExecutionStatus[AppSessionExecutionStatus.Canceled]:
        return true;
      default:
        return false;
    }
  }

  private getAppSessionAndReportsObservable(appSessionId: string): Observable<[IAppSession, IV2AppSessionReport, V1pre3ProjectCompact[]]> { 
    return this.getAppSessionObservableWithInclude(appSessionId, ['Children', 'Logs', 'RelatedUserWithAccess']).pipe( 
      switchMap((appSession: IAppSession) => { 
        let v2AppSession = appSession as V2AppSession;
        // Get the output projects from the appsession details properties in the response if it exists
        let outputProjects: V1pre3ProjectCompact[] = [];
        if (v2AppSession && v2AppSession.Properties && v2AppSession.Properties.Items) {
          let propertyItems = v2AppSession.Properties.Items;

          if (propertyItems.length > 0) {
            let projectProperty = propertyItems.find(item => item.Name === PropertyName.OUTPUT_PROJECTS);
            outputProjects = projectProperty ? projectProperty.ProjectItems : null;
          }
        }
        return forkJoin([ 
          of(appSession), 
          this.getAppSessionReportsObservable(appSessionId),
          // Use the output projects from the appsession details response if it exists to avoid an additional API call
          outputProjects ? of(outputProjects) : this.getAppSessionOutputProjects(appSessionId) 
        ]);
      }) 
    ); 
  } 

  private getAppSessionReportsObservable(appsessionId: string): Observable<IV2AppSessionReport> {

    return this.bsApi.getAppSessionReports(appsessionId).pipe(
      map((appSessionReports) => appSessionReports.Response),
      catchError((error) => {
        ConsoleLogger.logError(`Unable to get reports for analysis: ${appsessionId}. Exception: ${error}`);
        return EMPTY;
      })
    );
  }

  private getAppSessionOutputProjects(appSessionId: string): Observable<V1pre3ProjectCompact[]> {
    return this.getProperty(appSessionId, PropertyName.OUTPUT_PROJECTS, 1000)
      .pipe(
        map((outputProjectPropertyResponse) => outputProjectPropertyResponse.ProjectItems),
        catchError((error) => {
          ConsoleLogger.logError(`Unable to get output projects for analysis: ${appSessionId}.`);
          return of([]);
        })
      );
  }

  /**
   * to update the analysis that is being modified in the list of analyses.
   * @param analyses: analyses that is being modified
   * @param analysesList: list of analyses to update the analysis that is being modified
   * @param propertyUpdate: property changed
   */
  private getUpdatedAnalysesList(analyses: IAppSession, analysesList: IAppSession[], propertyUpdate: object): IAppSession[] {
    if (!isNullOrUndefined(analysesList)) {
      const idxSelectedAnalyses = analysesList.findIndex(r => r.Id === analyses.Id);
      if (!isNullOrUndefined(idxSelectedAnalyses) && idxSelectedAnalyses !== -1) {
        analysesList[idxSelectedAnalyses] = Object.assign(analysesList[idxSelectedAnalyses], propertyUpdate);
      }
    }
    return analysesList;
  }

  /**
   * set state based on page context. Update analyseslist and selected analyses if page context is
   * master list.
   * Updates analyseslist, selected analysis and the current analysis if page context is
   * details page.
   * @param propertyChange: property that are changing
   * @param analyses: the analyses acted upon
   * @param actionName: name of action based on service
   * @param error: error in case of partial success ( i.e in case of list of resources)
   * @param forceRefreshAnalysesList: whether to refresh analyses list if action was performed from list page
   */
  private setStateOnSuccess(propertyChange: object, analyses: IAppSession[], actionName: AnalysisStoreActions,
    error: any = null, forceRefreshAnalysesList?: boolean) {

    const { analysesList, selectedAnalyses, currentAnalysis } = this.getState();

    let newCurrentAnalyses = null; // currentAnalysis null if in any other page context than details
    let newAnalysesList = analysesList;
    let newSelectedAnalysesList = selectedAnalyses;

    if (this.pageContextService.isAnalysisMasterList()) {
      if (isNullOrUndefined(analysesList) || isNullOrUndefined(selectedAnalyses) || selectedAnalyses.length === 0) {
        return;
      }

      analyses.forEach(analysis => {
        newAnalysesList = this.getUpdatedAnalysesList(analysis, analysesList, propertyChange);
        newSelectedAnalysesList = this.getUpdatedAnalysesList(analysis, selectedAnalyses, propertyChange);
      });
    }

    if (this.pageContextService.isAnalysisDetails()) {
      if (analyses.length === 1) {
        newCurrentAnalyses = Object.assign(analyses[0], propertyChange);
      } else {
        newCurrentAnalyses = currentAnalysis;
      }
    }

    this.setState({
      analysesList: newAnalysesList,
      selectedAnalyses: newSelectedAnalysesList,
      currentAnalysis: newCurrentAnalyses,
      analysisStateError: error,
    },
      actionName);

    this.refreshSubject.next(forceRefreshAnalysesList);

  }

  /**
   * edit analysis name
   * @param newAnalysesName: new name
   * @param analyses: the analyses acted upon
   */
  private editAnalysesName(newAnalysesName: string, analyses: IAppSession): Observable<V2AppSession> {

    if (!analyses) {
      return throwError(ErrorMessages.ANALYSIS_EDIT.NO_ANALYSIS_SPECIFIED);
    }

    const params = {
      id: analyses.Id,
      payload: {
        Name: newAnalysesName
      }
    };

    return this.basespaceApi.PostV2AppsessionsId(params).pipe(
      // Retry in case of HTTP errors
      retryWhen(genericRetryWhen()),
      // To avoid a Flash of content, maintain a delay
      delay(observableEmitDelay),
    );
  }
}

// The action-names for this store, for logging/tracking.
export enum AnalysisStoreActions {
  LoadAnalysisDetails = 'LOAD_ANALYSIS',
  LoadAnalysesList = 'LOAD_ANALYSES_LIST',
  UpdateSelectedAnalyses = 'UPDATE_SELECTED_ANALYSES',
  EditAnalysesName = 'EDIT_ANALYSES_NAME',
  ClearSelectedAnalyses = 'CLEAR_SELECTED_ANALYSES',
  ChangeDeliveryStatus = 'CHANGE_DELIVERY_STATUS',
  DeleteAnalyses = 'DELETE_ANALYSES',
  CancelAnalyses = 'CANCEL_ANALYSES'
}